import { Permissions } from '../../../../models/enums/permissions';
import * as React from 'react';
import { connect } from 'react-redux';
import { fetchCreditPageData } from '../../../../actions/accounting-actions';
import { fetch } from '../../../../actions/utility-actions';
import { PageActions } from '../../../../components/form-elements/_index';
import { DealerInput } from '../../../../components/form-elements/DealerInput';
import { SelectInput } from '../../../../components/form-elements/SelectInput';
import { TextArea } from '../../../../components/form-elements/TextArea';
import { TextInput } from '../../../../components/form-elements/TextInput';
import { PageActionHeading } from '../../../../components/page-components/PageActionHeading';
import { PageBody } from '../../../../components/page-components/PageBody';
import { PageContent } from '../../../../components/page-components/PageContent';
import { PageHeading } from '../../../../components/page-components/PageHeading';
import { Table } from '../../../../components/tables/Table';
import { subscribeToRouteChange } from '../../../../hocs/PageWrapper';
import { CreditCategory } from '../../../../models/accounting/credit/credit-category';
import { CreditReason } from '../../../../models/accounting/credit/credit-reason';
import { CreditRequestCreate } from '../../../../models/accounting/credit/credit-request-create';
import { CreditLineItem } from '../../../../models/accounting/credit/credit-request-lineitem';
import { DivisionMetadata } from '../../../../models/accounting/enums/division';
import { SelectListItem } from '../../../../models/utilities/selectlist-item';
import { ServiceCenter } from '../../../../models/utilities/service-center';
import { CurrencyType } from '../../../../models/utilities/type-models';
import { serviceCenterSelectList } from '../../../../selectors/userdata-selectors';
import { AccountingSvc } from '../../../../services/accounting-service';
import { IApplicationState } from '../../../../store/IApplicationState';
import { AppUtils } from '../../../../utils/app-utils';
import { ModelValidator } from '../../../../utils/model-validator';
import { CreditLineItemEntryRow } from '../components/CreditLineItemEntryRow';
import { CreditLineItemSummary } from '../components/CreditLineItemSummary';
import '../CreditEntry.scss';
import { ApiEndpoints } from '../../../../utils/app-constants';

// #region Component State

const defaultState = {
    tmpCredit           : new CreditRequestCreate(),
    divisions           : AppUtils.enumToSelectList(DivisionMetadata),
    errorMsgs           : {} as any,
    subtotal            : 0.00,
    total               : 0.00,
    hasTriedSave        : false,
    dealerTaxBasis      : 0,
    dealerCurrencyCode  : '' as CurrencyType,
    dealerIsTaxExempt   : false,
    fetchingDealerPrefs : false,
    // Once there is a dealer to make a credit for, the page says so, as the top bar cannot.  Until then it has no title
    pageTitle           : ''
};

type State = Readonly<typeof defaultState>;

// #endregion

// #region Component Props

interface IProps extends IBaseSceneProps {
    // Event Handles
    fetchCreditPageData : any;
    reduxDispatch       : any;
    getDealerPrefs      : any;
    // Data
    creditCategories    : CreditCategory[];
    creditReasons       : CreditReason[];
    allServiceCenters   : Array<SelectListItem<string>>;
    activeServiceCenter : ServiceCenter;
}

// #endregion

export class CreditEntrySceneComponent extends React.Component<IProps, State> {

    public accountingSvc : AccountingSvc = new AccountingSvc();
    public state : State = defaultState;
    private itemSummaryRef : any;

    public componentDidMount() {
        this.props.fetchCreditPageData();
        this.onReset();
    }

    public render() {

        return <PageContent pageName="[ACCOUNTING] Credit Entry">
                    <PageHeading 
                        shouldStick={ true }
                        title={ this.state.pageTitle }>
                        <PageActions
                            id="credit-entry-form"
                            onEdit={ this.onSave }
                            editLabel="Save"
                            editPermissions={[
                                Permissions.InvoicingCredits
                            ]} 
                            onDelete={ this.onReset }
                            deleteLabel="Reset"
                            deletePermissions={[
                                Permissions.InvoicingCredits
                            ]} 
                        />   
                    </PageHeading>
                    <PageBody>
                        <form autoComplete="off">
                            <div className="row">
                                <div className="col-md-6">
                                    <DealerInput
                                        id="creditDealer"
                                        label="Dealer"
                                        dispatch={ this.props.reduxDispatch }
                                        onChange={this.onDealerChange}
                                        error={this.state.errorMsgs.dealerId}
                                        value={this.state.tmpCredit.dealerId} />
                                </div>
                                <div className="col-md-3">
                                    <TextInput
                                        type="text"
                                        id="creditCurrency"
                                        label="Currency"
                                        onChange={() => { return; }}
                                        isLoading={this.state.fetchingDealerPrefs}
                                        value={ this.state.dealerCurrencyCode }
                                        disable={true} />
                                </div>
                                <div className="col-md-3">
                                    <TextInput
                                        type="text"
                                        id="taxBasis"
                                        label="Tax Rate"
                                        onChange={() => { return; }}
                                        isLoading={this.state.fetchingDealerPrefs}
                                        value={ this.state.dealerTaxBasis + '%' }
                                        disable={true} />
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6">
                                    <SelectInput
                                        id="serviceCenter"
                                        label="Service Center"
                                        onChange={ this.onServiceCenterChange }
                                        options={ this.props.allServiceCenters }
                                        value={ this.state.tmpCredit.serviceCenterId }
                                        disable={ this.shouldDisableServiceCenter() }/>
                                </div>
                                <div className="col-md-6">
                                    <SelectInput
                                        id="testinput"
                                        label="Division"
                                        onChange={this.onDivisionChange}
                                        error={this.state.errorMsgs.division}
                                        options={this.state.divisions}
                                        value={this.state.tmpCredit.division} />
                                </div>
                            </div>

                            <div className="row divider-row">
                                <div className="col">
                                    <PageActionHeading
                                        id="addlineitem"
                                        heading={[
                                            'Line Items ',
                                            !AppUtils.isArray(this.state.errorMsgs.items) && 
                                            !AppUtils.IsEmpty(this.state.errorMsgs)
                                                ? <span key="itemserror" className="page-action-error">
                                                    { this.state.errorMsgs.items }
                                                </span>
                                                : null
                                        ]}                                        
                                        actionText="Add"
                                        actionIcon="la-plus-circle"
                                        actionDisable={ 
                                            !(
                                                this.props.creditCategories && this.props.creditCategories.length > 0 &&
                                                this.props.creditReasons && this.props.creditReasons.length > 0
                                            )
                                        }
                                        actionClick={this.onAddRow} />
                                    <Table
                                        icon="la-exclamation"
                                        transparentBackground={true}
                                        header={[
                                            'Reason',
                                            'Category',
                                            'Description',
                                            'Amount',
                                            'Delete',
                                        ]}
                                        noResultsMessage="No line items added yet, please add one to get started.">
                                        {
                                            this.state.tmpCredit.items.map((itm: CreditLineItem, idx: number) => (
                                                <CreditLineItemEntryRow
                                                    key={ idx }
                                                    reasons={ this.props.creditReasons }
                                                    categories={ this.props.creditCategories }
                                                    errorItem={ this.state.errorMsgs.items 
                                                        ? this.state.errorMsgs.items[idx]
                                                        : null 
                                                    }
                                                    onDataChange={ this.onLineItemChange }
                                                    onDeleteRow={ this.onDeleteRow }
                                                    dealerCurrencyCode={ this.state.dealerCurrencyCode }
                                                    lineItem={ itm }
                                                    idx={ idx.toString() } />
                                            ))
                                        }
                                    </Table>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col">
                                    <TextArea 
                                        id="credit-note"
                                        className="credit-entry-note"
                                        label="Note"
                                        allowReturn={ true }
                                        error={this.state.errorMsgs.note}
                                        value={ this.state.tmpCredit.note }
                                        onChange={ this.onNoteChange } />
                                </div>
                                <div className="col">
                                    <CreditLineItemSummary
                                        subtotal={this.state.subtotal}
                                        freight={this.state.tmpCredit.freight}
                                        tax={this.state.tmpCredit.tax}
                                        total={this.state.total}
                                        ref={(instance) => { this.itemSummaryRef = instance; }}
                                        isDealerTaxExempt={this.state.dealerIsTaxExempt}
                                        currencyCode={this.state.dealerCurrencyCode}
                                        onFreightChange={this.onFreightChange}
                                        onTaxChange={this.onTaxChange} />          
                                </div>
                            </div>
                            
                            

                        </form>
                    </PageBody>
                </PageContent>;
    }

    private onServiceCenterChange = (e: any) => {
        e.preventDefault();

        const tmp = this.state.tmpCredit;
        tmp.serviceCenterId = e.target.value;

        this.setState({
            tmpCredit: tmp
        });
    }

    private shouldDisableServiceCenter = () => {

        if ((this.state.tmpCredit.dealerId.length === 0)) {
            return true;
        }
        else {
            return this.props.activeServiceCenter.name !== "ALL";
        }   
               
    }

    private onDealerChange = (e: any) => {

        this.setState({
            fetchingDealerPrefs: true
        });
        
        this.getDealerPrefs(e.id);
    }

    private onNoteChange = (e: any) => {

        const tmp = this.state.tmpCredit;
        tmp.note = e.target.value;

        this.setState({
            tmpCredit: tmp
        });

    }

    private onDivisionChange = (e: any) => {
        e.preventDefault();

        const tmp = this.state.tmpCredit;
        if (isNaN(parseInt(e.target.value, 10))) {
            tmp.division = undefined;
        }
        else {
            tmp.division = parseInt(e.target.value, 10);
        }

        this.setState({
            tmpCredit: tmp
        }, this.validateData);
    }

    private onLineItemChange = (e: any, currencyValue: number) => {
        e.preventDefault();

        if (!e.target.id) {
            return;
        }

        (this.state.tmpCredit.items[e.target.id] as any)[e.target.name] = 
            (currencyValue ? currencyValue : e.target.value);

        if (e.target.name === "reasonId") {

            const parsedReasonId = Number.parseInt(e.target.value, 10);
            (this.state.tmpCredit.items[e.target.id] as any)[e.target.name] = parsedReasonId;

            const r = this.props.creditReasons.find((itm: CreditReason) => {
                const intValue = parsedReasonId;
                return itm.id === intValue;
            });

            // Disable the category for anything with a revenue code
            if (r && r.revenueCode) {    
                this.state.tmpCredit.items[e.target.id].categoryId = undefined;
                this.state.tmpCredit.items[e.target.id].disabledCategory = true;
            }
            else {
                this.state.tmpCredit.items[e.target.id].disabledCategory = false;
            }

            // Update the default description based on reason.
            this.state.tmpCredit.items[e.target.id].description = r ? r.defaultDescription : '';
        }
        else if(e.target.name === "categoryId") {
            const parsedCategoryId = Number.parseInt(e.target.value, 10);
            (this.state.tmpCredit.items[e.target.id] as any)[e.target.name] = parsedCategoryId;
        }
        
        this.setState({
            tmpCredit: this.state.tmpCredit
        }, () => {
            this.addLineAmount();
            this.validateData();
        } );
    }

    private onFreightChange = (e: any, newValue: number) => {
        e.preventDefault();

        const tmp = this.state.tmpCredit;
        tmp.freight = newValue;
        this.setState({
            tmpCredit: tmp
        }, this.updateTotal);
    }
    
    private onTaxChange = (e: any, newValue: number) => {
        const tmp = this.state.tmpCredit;
        tmp.tax = newValue;
        this.setState({
            tmpCredit: tmp
        }, this.updateTotal);   
    }

    private validateData = () => {
        if (this.state.hasTriedSave) {
            const msgs = ModelValidator.validate(this.state.tmpCredit);
            if (AppUtils.IsEmpty(msgs)) {
                return true;
            }

            this.setState({
                errorMsgs: msgs
            });
        }
        return false;
    }

    private addLineAmount = () => {

        const amount = this.state.tmpCredit.items.reduce((curr : number, itm: CreditLineItem) => {
            const amt = (itm.amount && !isNaN(itm.amount)) ? itm.amount : 0.00;
            return Number(curr) + Number(amt);
        }, 0);

        this.setState({
            subtotal: amount
        }, this.updateTotal);

    }

    private onDeleteRow = (idx: number, e: any) => {
        e.preventDefault();
        
        if (!idx) {
            return;
        }

        this.state.tmpCredit.items.splice(idx, 1);
        this.setState({
            tmpCredit: this.state.tmpCredit
        }, this.addLineAmount);
        
    }

    private onAddRow = (e: any) => {
        e.preventDefault();

        const tmp = this.state.tmpCredit;
        tmp.items.push(new CreditLineItem());
        this.setState({
            tmpCredit: tmp
        }, this.validateData);
    }

    private onSave = (e: any) => {
        e.preventDefault();
        this.setState({
            hasTriedSave: true    
        }, async () => {
            const isValid = this.validateData();
            if (isValid) {
                await this.accountingSvc.createCredit(this.state.tmpCredit);
            }
        });
    }

    private updateTotal = () => {
        const subtotal = this.state.subtotal;
        const { tax, freight } = this.state.tmpCredit;

        const t = subtotal + tax + freight;
        this.setState({
            total: t
        });
    }

    private onReset = () => {
        const tmp = new CreditRequestCreate();
        this.itemSummaryRef.reset();

        // If you aren't all, the pickup gets created in your scope
        // service center always.
        if (this.props.activeServiceCenter.name !== "ALL") {
            tmp.serviceCenterId = this.props.activeServiceCenter.name;        
        }

        this.setState({
            ...defaultState,
            tmpCredit: tmp
        });
    }

    private getDealerPrefs = (dealerId: string) => {
        try {
            if (dealerId && dealerId.length > 0) {

                const casedDealerId = dealerId.toUpperCase();
                const prom = this.props.getDealerPrefs(casedDealerId);

                prom.then((data: any) => {
        
                    const { 
                        currencyId, 
                        isTaxExempt, 
                        taxRate, 
                        name, 
                        serviceCenter 
                    } = data;        

                    const tmp = this.state.tmpCredit;
                    tmp.dealerId        = casedDealerId;
                    tmp.currencyId    = currencyId;

                    // If you aren't all, the pickup gets created in your scope
                    // service center always.
                    if (this.props.activeServiceCenter.name === "ALL") {
                        tmp.serviceCenterId   = serviceCenter;
                    }
                    
                    if (isTaxExempt) {
                        tmp.tax = 0.00;
                    }
        
                    this.setState({
                        tmpCredit: tmp,
                        dealerTaxBasis : taxRate,
                        dealerCurrencyCode : currencyId,
                        dealerIsTaxExempt: isTaxExempt,
                        fetchingDealerPrefs: false,
                        pageTitle: `Credits for ${ name }`
                    }, () => {
                        this.validateData();
                    });
                })
                .catch(() => {
                    
                    const tmp = this.state.tmpCredit;
                    tmp.dealerId = '';
                    tmp.currencyId = '';

                    this.setState({
                        fetchingDealerPrefs: false,
                        dealerCurrencyCode: '',
                        dealerTaxBasis: 0,
                        tmpCredit: tmp,
                        pageTitle: ''
                    });

                });
            }
        }
        catch (err) {
            console.log("no dealer found");
        }
    }

}

const mapStateToProps = (state: IApplicationState) => {
    return {
        creditCategories: state.accounting.creditCategories.data,
        creditReasons: state.accounting.creditReasons.data,
        allServiceCenters: serviceCenterSelectList(state),
        activeServiceCenter: state.userdata.activeServiceCenter
    };
};

const mapDispatchToProps = (dispatch: any) => {
    return {
        fetchCreditPageData: () => dispatch(fetchCreditPageData()),
        getDealerPrefs: (dealerId: number) => dispatch(fetch(ApiEndpoints.DEALERS_BY_ID.formattify(dealerId), 'GET')),
        reduxDispatch: dispatch
    };
};

const CreditEntryScene = connect(
    mapStateToProps, 
    mapDispatchToProps
)(subscribeToRouteChange(CreditEntrySceneComponent));

export { CreditEntryScene };
